import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { makeStyles } from "@fluentui/react-components";
import { AlyzeLoadingSpinner } from "./componentsV1/shared";
import { createApiClient, setApiClientInstance } from './services/apiClientFactory';
import { HostedAuthProvider } from './auth/components/HostedAuthContext';
import { RequireSignup, RequireSignIn } from './auth/components';
import { LocationLayout } from './componentsV1/layouts';
import { RootLayout } from './layouts';
import { ScrollToTop } from './utilities/ScrollToTop';
import { authEventListener } from './services/authEventListener';
import { WebinarBundleCartProvider } from './contexts/WebinarBundleCartContext';

// Lazy load all route components for code splitting
const AuthCallback = lazy(() => import('./componentsV1/AuthCallback'));
const SignIn = lazy(() => import('./auth/components/HostedAuthContext/test/SignIn').then(m => ({ default: m.SignIn })));
const SignUp = lazy(() => import('./auth/components/HostedAuthContext/test/SignUp').then(m => ({ default: m.SignUp })));
const EmailOnlySignup = lazy(() => import('./auth/components/HostedAuthContext/test/EmailOnlySignup').then(m => ({ default: m.EmailOnlySignup })));

// New Feature-Based Components (Tailwind only)
const Home = lazy(() => import('@features/home'));
const AlyZeMembershipWaitlistPage = lazy(() => import('./features/waitlist/alyze-membership-waitlist/AlyZeMembershipWaitlistPage').then(m => ({ default: m.AlyZeMembershipWaitlistPage })));
const AlyzeAssessmentWaitlistPage = lazy(() => import('./features/waitlist/alyze-assessment-waitlist/AlyzeAssessmentWaitlistPage').then(m => ({ default: m.AlyzeAssessmentWaitlistPage })));
const MembershipWaitlistConfirmPage = lazy(() => import('./features/info/membership-waitlist/MembershipWaitlistConfirmPage').then(m => ({ default: m.MembershipWaitlistConfirmPage })));
const AssessmentWaitlistConfirmPage = lazy(() => import('./features/info/assessment-waitlist/AssessmentWaitlistConfirmPage').then(m => ({ default: m.AssessmentWaitlistConfirmPage })));

// V1 (Legacy) Legal Components
const PrivacyPolicyV1 = lazy(() => import('./componentsV1/company/PrivacyPolicy/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfServiceV1 = lazy(() => import('./componentsV1/company/TermsOfService/TermsOfService').then(m => ({ default: m.TermsOfService })));

// V1 (Legacy) Marketing Components
const LocationV1 = lazy(() => import('./componentsV1/marketing/locations/bountiful').then(m => ({ default: m.Location })));

// V1 (Legacy) Shop Components - only the personalized-supplements checkout is
// exposed for now (CartPage and CheckoutPage are unused but kept for re-enable).
const PersonalizedCheckoutPage = lazy(() => import('./componentsV1/shop/PersonalizedCheckoutPage').then(m => ({ default: m.PersonalizedCheckoutPage })));

// Other components
const EpigeneticOfferPage = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticOfferPage })));
const EpigeneticDealPage = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticDealPage })));
const EpigeneticsCPage = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticsCPage })));
const DemographicInfoForm = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.DemographicInfoForm })));
const EpigeneticOfferCheckout = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticOfferCheckout })));
const EpigeneticOfferThankYou = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticOfferThankYou })));
const EpigeneticEmailOnlyCheckout = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticEmailOnlyCheckout })));
const EpigeneticEmailOnlyThankYou = lazy(() => import('./componentsV1/marketing/offers/epigenetic').then(m => ({ default: m.EpigeneticEmailOnlyThankYou })));
const EpigeneticsBundle = lazy(() => import('./componentsV1/webinar/epigenetic-bundle/components/EpigeneticsBundle').then(m => ({ default: m.EpigeneticsBundle })));
const WebinarCheckout = lazy(() => import('./componentsV1/webinar/shared/components/checkout/WebinarCheckout').then(m => ({ default: m.WebinarCheckout })));
const WebinarThankYou = lazy(() => import('./componentsV1/webinar/shared/components/checkout/WebinarThankYou').then(m => ({ default: m.WebinarThankYou })));

const useStyles = makeStyles({
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '16px',
        padding: '24px',
    }
});

function App() {
    const auth = useAuth();
    const location = useLocation();
    const styles = useStyles();

    // Initialize API client
    useEffect(() => {
        const apiClient = createApiClient();
        setApiClientInstance(apiClient);
    }, []);

    // Register auth event listeners
    useEffect(() => {
        if (auth.events) {
            auth.events.addUserLoaded(authEventListener.onUserLoaded);
            auth.events.addUserUnloaded(authEventListener.onUserUnloaded);
            auth.events.addUserSignedOut(authEventListener.onUserSignedOut);

            return () => {
                auth.events.removeUserLoaded(authEventListener.onUserLoaded);
                auth.events.removeUserUnloaded(authEventListener.onUserUnloaded);
                auth.events.removeUserSignedOut(authEventListener.onUserSignedOut);
            };
        }
    }, [auth.events]);

    // Auto-redirect to login if not authenticated (only for checkout)
    useEffect(() => {
        const isCheckoutPath = location.pathname === '/checkout';
        
        // If on checkout page, require authentication
        if (isCheckoutPath && !auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
            const fullPath = location.pathname + location.search + location.hash;
            sessionStorage.setItem('auth.redirectPath', fullPath);
            auth.signinRedirect().catch((error) => {
                console.error("Login redirect failed:", error);
            });
            return;
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.signinRedirect, location]);

    if (auth.isLoading || auth.activeNavigator) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Loading..." />
            </div>
        );
    }

    if (auth.error) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message={`Authentication error: ${auth.error.message}`} />
                <div>Redirecting to sign in...</div>
            </div>
        );
    }

    // Show loading while redirecting to login for checkout
    if (!auth.isAuthenticated && location.pathname === '/checkout') {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Redirecting to sign in..." />
            </div>
        );
    }

    return (
        <div>
            <ScrollToTop />
            <Suspense fallback={
                <div className={styles.loadingContainer}>
                    <AlyzeLoadingSpinner message="Loading..." />
                </div>
            }>
                <Routes>
                    {/* Auth callback route - accessible without authentication */}
                    <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Membership routes with minimal layout (no header/footer) */}
                {/*<Route path="/membership" element={<MembershipLayout />}>*/}
                {/*    <Route index element={<Navigate to="/membership/checkout" replace />} />*/}
                {/*    <Route path="checkout" element={<MembershipCheckout />} />*/}
                {/*    <Route path="thank-you" element={<MembershipThankYou />} />*/}
                {/*</Route>*/}

                {/* Location routes with minimal layout (no header/footer) */}
                <Route path="/locations" element={<LocationLayout />}>
                    <Route index element={<Navigate to="/locations/bountiful" replace />} />
                    <Route path="bountiful" element={<LocationV1 />} />
                </Route>

                {/* Hosted Auth Test Routes */}
                <Route path="/auth/hosted/test/signin" element={
                    <HostedAuthProvider>
                        <SignIn />
                    </HostedAuthProvider>
                } />
                <Route path="/auth/hosted/test/signup" element={
                    <HostedAuthProvider>
                        <SignUp />
                    </HostedAuthProvider>
                } />
                <Route path="/auth/hosted/test/email-only-signup" element={
                    <HostedAuthProvider>
                        <EmailOnlySignup />
                    </HostedAuthProvider>
                } />

                {/* Epigenetic offer routes - all standalone (no header/footer) */}
                {/* Demographic flow (original) */}
                <Route path="/offers/epigenetic" element={<EpigeneticOfferPage />} />
                <Route path="/offers/epigenetic-deal" element={<EpigeneticDealPage />} />
                <Route 
                    path="/offers/epigenetic/demographic-info" 
                    element={
                        <RequireSignup redirectPath="/offers/epigenetic/demographic-info">
                            <DemographicInfoForm />
                        </RequireSignup>
                    } 
                />  
                <Route 
                    path="/offers/epigenetic/checkout" 
                    element={
                        <RequireSignIn redirectPath="/offers/epigenetic/checkout">
                            <EpigeneticOfferCheckout />
                        </RequireSignIn>
                    } 
                />
                <Route 
                    path="/offers/epigenetic/thank-you" 
                    element={
                        <RequireSignIn redirectPath="/offers/epigenetic/thank-you">
                            <EpigeneticOfferThankYou />
                        </RequireSignIn>
                    } 
                />

                {/* Email-only flow (new) */}
                <Route path="/offers/epigenetic/email-only/epigenetic-c" element={<EpigeneticsCPage />} />
                <Route path="/offers/epigenetic/email-only/checkout" element={<EpigeneticEmailOnlyCheckout />} />
                <Route 
                    path="/offers/epigenetic/email-only/thank-you" 
                    element={
                        <HostedAuthProvider>
                            <EpigeneticEmailOnlyThankYou />
                        </HostedAuthProvider>
                    } 
                />

                {/* Legal routes - standalone (no header/footer) for 3rd party linking */}
                <Route path="/legal/terms-of-service" element={<TermsOfServiceV1 />} />
                <Route path="/legal/privacy-policy" element={<PrivacyPolicyV1 />} />

                {/* Webinar bundle routes - standalone (no header/footer) */}
                <Route path="/webinar" element={<Navigate to="/webinar/epigenetic-bundle" replace />} />
                <Route path="/webinar/epigenetic-bundle" element={
                    <WebinarBundleCartProvider>
                        <EpigeneticsBundle />
                    </WebinarBundleCartProvider>
                } />
                <Route path="/webinar/epigenetic-bundle/checkout" element={<WebinarCheckout />} />
                <Route 
                    path="/webinar/epigenetic-bundle/thank-you" 
                    element={
                        <HostedAuthProvider>
                            <WebinarThankYou />
                        </HostedAuthProvider>
                    } 
                />

                {/* New Store routes with RootLayout (Tailwind-converted pages only) */}
                <Route path="/" element={<RootLayout />}>
                    <Route index element={<Home />} />
                    <Route path="home" element={<Home />} />

                    {/* Waitlist Pages (HubSpot forms) */}
                    <Route path="alyze-membership-waitlist" element={<AlyZeMembershipWaitlistPage />} />
                    <Route path="alyze-assesment-waitlist" element={<AlyzeAssessmentWaitlistPage />} />

                    {/* Info Pages (post-form confirmation) */}
                    <Route path="info/membership-waitlist" element={<MembershipWaitlistConfirmPage />} />
                    <Route path="info/assessment-waitlist" element={<AssessmentWaitlistConfirmPage />} />
                    
                    {/* Legal Pages (NEW Tailwind versions) */}
                    {/* <Route path="legal/terms-of-service" element={<TermsOfService />} />
                    <Route path="legal/privacy-policy" element={<PrivacyPolicy />} /> */}
                    
                    {/* V1 Legal Pages (also accessible via RootLayout) */}
                    <Route path="terms-of-service" element={<TermsOfServiceV1 />} />
                    <Route path="privacy-policy" element={<PrivacyPolicyV1 />} />
                    
                    {/* Catch-all route inside RootLayout: send unknown URLs back
                        to the marketing home page. */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Route>

                {/* V1 (Legacy) Store routes - only the personalized-supplements checkout is exposed.
                    Rendered without StoreLayoutV1 so it has no header/footer. */}
                <Route path="/shop/personalized-checkout" element={<PersonalizedCheckoutPage />} />
                <Route path="/shop" element={<Navigate to="/" replace />} />
                <Route path="/shop/*" element={<Navigate to="/" replace />} />

                {/* Legacy /v1/* routes have been retired. Anything under /v1
                    redirects to the new marketing home page. */}
                <Route path="/v1" element={<Navigate to="/" replace />} />
                <Route path="/v1/*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
        </div>
    );
}

export default App;